/**
 * Subscription lockout state (CVA-4668).
 *
 * The API is the only authority on who is locked out. Two signals feed this
 * store, both from the API:
 *  1. GET /subscription/lockout-status — asked once per signed-in user and
 *     organization, so the lock screen shows on first paint.
 *  2. Any HTTP 402 with a `SUBSCRIPTION_*` errorCode — so a lockout that starts
 *     mid-session is picked up by whichever call hits it first.
 *
 * The portal never derives the lockout from subscription fields itself: doing
 * so is how a cancelled organization still inside its paid period ended up
 * locked in the portal while the API let it through.
 *
 * The state belongs to one user in one organization (`owner`). Logging out,
 * logging in as someone else or a guardian switching to another school's
 * student all change the owner, and a state recorded for a different owner is
 * never reported — otherwise a lockout would follow the browser tab into the
 * next session.
 *
 * The axios interceptors live outside React, so this is a tiny observable store
 * rather than Redux, read through useSubscriptionLock().
 */
import { getOrganizationId, getUserId } from './tokenManager';

export const SUBSCRIPTION_LOCK_CODES = {
  TRIAL_EXPIRED: 'SUBSCRIPTION_TRIAL_EXPIRED',
  CANCELLED: 'SUBSCRIPTION_CANCELLED',
  PAYMENT_REQUIRED: 'SUBSCRIPTION_PAYMENT_REQUIRED',
} as const;

export type SubscriptionLockCode =
  (typeof SUBSCRIPTION_LOCK_CODES)[keyof typeof SUBSCRIPTION_LOCK_CODES];

export interface SubscriptionLockDetails {
  status?: string;
  unpaidInvoiceCount?: number;
  outstandingAmount?: number;
  earliestDueDate?: string;
  invoiceNumber?: string;
  trialEndDate?: string;
  accessEndedAt?: string;
}

export interface SubscriptionLockState {
  /** Who this state belongs to — `userId:organizationId`, or null when signed out. */
  owner: string | null;
  /** Whether the API has answered for this owner yet. */
  resolved: boolean;
  locked: boolean;
  code: SubscriptionLockCode | null;
  message: string | null;
  details: SubscriptionLockDetails | null;
}

/** Body of GET /subscription/lockout-status. */
export interface SubscriptionLockStatusResponse {
  locked: boolean;
  errorCode: string | null;
  message: string | null;
  details: SubscriptionLockDetails | null;
}

const unresolved = (owner: string | null): SubscriptionLockState => ({
  owner,
  resolved: false,
  locked: false,
  code: null,
  message: null,
  details: null,
});

/** The signed-in user and organization, from the access token. */
export const getSubscriptionLockOwner = (): string | null => {
  const userId = getUserId();
  return userId ? `${userId}:${getOrganizationId() ?? ''}` : null;
};

let current: SubscriptionLockState = unresolved(null);
const listeners = new Set<(s: SubscriptionLockState) => void>();

const emit = () => listeners.forEach((l) => l(current));

export const isSubscriptionLockCode = (code: unknown): code is SubscriptionLockCode =>
  typeof code === 'string' && (Object.values(SUBSCRIPTION_LOCK_CODES) as string[]).includes(code);

/**
 * The state for whoever is signed in now. A state recorded for another owner
 * reads as "not answered yet", never as that owner's lock.
 */
export const getSubscriptionLock = (): SubscriptionLockState => {
  const owner = getSubscriptionLockOwner();
  return current.owner === owner ? current : unresolved(owner);
};

/** Called by the axios interceptors when the API answers 402 with a lockout code. */
export const setSubscriptionLock = (
  code: SubscriptionLockCode,
  message?: string | null,
  details?: SubscriptionLockDetails | null,
): void => {
  const owner = getSubscriptionLockOwner();
  // Ignore no-op updates so we don't re-render on every blocked call.
  if (current.owner === owner && current.locked && current.code === code) return;
  current = {
    owner,
    resolved: true,
    locked: true,
    code,
    message: message ?? null,
    details: details ?? null,
  };
  emit();
};

/** Record the answer from GET /subscription/lockout-status for `owner`. */
export const applySubscriptionLockStatus = (
  owner: string | null,
  status: SubscriptionLockStatusResponse | null,
): void => {
  // The user changed while the request was in flight — the answer is stale.
  if (owner !== getSubscriptionLockOwner()) return;
  const locked = !!status?.locked && isSubscriptionLockCode(status.errorCode);
  current = {
    owner,
    resolved: true,
    locked,
    code: locked ? (status!.errorCode as SubscriptionLockCode) : null,
    message: locked ? status!.message ?? null : null,
    details: locked ? status!.details ?? null : null,
  };
  emit();
};

/** Forget everything — on sign-out, sign-in and organization switch. */
export const resetSubscriptionLock = (): void => {
  current = unresolved(getSubscriptionLockOwner());
  emit();
};

export const subscribeSubscriptionLock = (
  listener: (s: SubscriptionLockState) => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
